<template>
  <div :class="['app', routeName]" :data-test-id="routeName">
    <router-view v-slot="{ Component }">
      <transition name="fade-transition" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>

    <transition name="fade-transition" mode="out-in" :duration="100">
      <ViewLoading
        v-if="showStartupProgress || authenticationFailed"
        :text="$t('notification.setupInProgress')"
        :error="authenticationFailed"
        data-test-id="registration-loader"
      />
    </transition>

    <div class="notification-container">
      <transition-group
        class="notification-list"
        name="notification-transition"
        tag="div"
        @mouseenter="$store.dispatch('notification/hold')"
        @mouseleave="$store.dispatch('notification/unhold')"
      >
        <NotificationCard
          v-for="notification in notificationsRight"
          :key="notification.id"
          :spinner="notification.spinner"
          :data-test-id="notification.testId"
        >
          <template #title>
            <i18n-t :keypath="notification.message.path" tag="span" v-if="notification.message.path">
              <template :key="key" #[key] v-for="(value, key) in notification.message.args">
                <q>{{ value }}</q>
              </template>
            </i18n-t>
            <span v-else>{{ notification.message }}</span>
          </template>

          <template #actions>
            <Btn
              v-if="notification.undoAction"
              @click="notification.undoAction"
              color="dark"
              size="medium"
              data-test-id="restoreDeleteButton"
            >
              {{ $t('action.undo') }}
            </Btn>

            <Btn
              v-for="(action, index) in notification.actions"
              :key="index"
              @click="action.action"
              color="dark"
              size="medium"
            >
              {{ action.label }}
            </Btn>

            <Btn
              v-if="!notification.hideCloseButton"
              :title="$t('action.close')"
              @click="$store.dispatch('notification/remove', notification.id)"
              color="dark"
              icon
              data-test-id="notification-close-button"
            >
              <CloseIcon />
            </Btn>
          </template>
        </NotificationCard>
      </transition-group>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { NotificationState } from '@/store/types';
import { mapState, mapGetters } from 'vuex';
import shortcutMixin, { ShortcutConfig } from '@shortcuts/shortcutMixin';
import { DebouncedFunc, debounce } from 'lodash-es';
import { closeChannel, initChannel } from '@/broadcast';
import CloseIcon from '@/components/Icons/CloseIcon.vue';
import ViewLoading from '@components/ViewLoading.vue';

export default defineComponent({
  name: 'App',

  mixins: [shortcutMixin],

  components: {
    CloseIcon,
    ViewLoading,
  },

  shortcuts(): ShortcutConfig {
    return {
      'go:back': () => this.$router.go(-1),
      'go:forward': () => this.$router.go(1),
    };
  },

  data(): {
    showStartupProgress: boolean;
    authenticationFailed?: boolean; // vuex
    startUpInProgress?: boolean; // vuex
    debouncedShowStartUpProgress: DebouncedFunc<(value: boolean) => void>;
  } {
    return {
      showStartupProgress: false,
      debouncedShowStartUpProgress: null,
    };
  },

  computed: {
    ...mapState<NotificationState>('notification', {
      notificationsRight: (state) => state.notifications.filter((notification) => notification.position === 'right'),
    }),
    ...mapState('user', ['authenticationFailed']),
    ...mapGetters('user', ['startUpInProgress']),
    routeName() {
      return `route-${String(this.$route.name)}`;
    },
  },

  methods: {
    setStartUpShowState(value: boolean) {
      this.showStartupProgress = value;
    },

    showOfflineMessage() {
      this.$store.dispatch('notification/remove', 'online');
      this.$store.dispatch('notification/add', {
        message: this.$t('notification.offline'),
        timeout: false,
        id: 'offline',
      });
    },
    hideOfflineMessage() {
      this.$store.dispatch('notification/remove', 'offline');
      this.$store.dispatch('notification/add', {
        message: this.$t('notification.online'),
        id: 'online',
      });
    },
  },

  watch: {
    startUpInProgress(value) {
      this.debouncedShowStartUpProgress(value);
    },
  },

  created() {
    // Debouncing showing the startup-in-progress overlay, so it's not shown at all if requests finish fast
    this.debouncedShowStartUpProgress = debounce(this.setStartUpShowState, 1000);

    window.addEventListener('offline', this.showOfflineMessage);

    window.addEventListener('online', this.hideOfflineMessage);

    this.$store.dispatch('tracking/requestInitialConsent');

    initChannel();
  },

  beforeUnmount() {
    this.debouncedShowStartUpProgress.cancel();

    window.removeEventListener('offline', this.showOfflineMessage);

    window.removeEventListener('online', this.hideOfflineMessage);

    closeChannel();
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
#app {
  height: 100%;
}

.app {
  height: 100%;
  background: colors.$color-white;
}

// Same width as the max-width of a notification.
// Ensures that leave-animated notifications keep their width
.notification-container {
  width: settings.$notification-max-width;
  pointer-events: none;
  position: fixed;
  top: 16px;
  left: auto;
  z-index: settings.$tooltip-z-index + 1; // make sure they appear on top of tooltips
  right: 24px;
}

.notification-list {
  position: relative;
  height: 0;
  display: grid;
  grid-gap: 8px;
}
</style>
